import { createContext, useCallback, useContext } from 'react';
import { useCookies } from 'react-cookie';

import ApolloAuth from '../services/apolloAuth';
import Session, { ACCESS_KEYS, REFRESH_KEY } from '../services/session';

const AuthContext = createContext({});

const AuthProvider = ({ children }) => {
  // O `useCookies` não é usado para escrever — quem escreve é o `Session`, para
  // que o interceptor do axios enxergue exatamente os mesmos valores. Assinar os
  // cookies aqui serve só para o React re-renderizar quando a sessão muda.
  const [cookies] = useCookies([...ACCESS_KEYS, REFRESH_KEY]);

  const signIn = useCallback(
    async ({ newToken, newRefreshToken, newUserImage, newUserName }) => {
      Session.save({
        token: newToken,
        refreshToken: newRefreshToken,
        userImage: newUserImage,
        userName: newUserName,
      });
    },
    []
  );

  const signOut = useCallback(async () => {
    const refreshToken = Session.getRefreshToken();

    Session.clear();

    // Revogar do lado da Apolo é o que realmente encerra a sessão; se a chamada
    // falhar, o cookie já foi apagado e o admin está fora deste navegador.
    if (refreshToken) {
      await ApolloAuth.logout({ refreshToken }).catch(() => undefined);
    }
  }, []);

  const updateUserImage = useCallback(async imageUrl => {
    Session.setUserImage(imageUrl);
  }, []);

  const updateUserName = useCallback(async userName => {
    Session.setUserName(userName);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        userImage: cookies.userImage,
        isLoggedIn: !!cookies.token || !!cookies.refreshToken,
        userType: cookies.userType,
        userName: cookies.userName,
        userToken: cookies.token,
        loading: false,
        signIn,
        signOut,
        updateUserImage,
        updateUserName,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export { AuthProvider, useAuth };
